import { Condicion, ConteoCriterios, OperadorCc } from "src/app/shared/model/calidad-codigo/calidad-codigo.model";

/**
 * Calidad de código: cómo se leen en pantalla los números y las fechas del
 * módulo. Funciones puras, sin Angular, compartidas por el tablero, el
 * detalle, los reportes y los PDF.
 *
 * SonarQube manda cada medida como texto crudo ("99.7", "1.0", "7582"); las
 * fechas llegan en UTC con `Z` y se enseñan en la hora del centro de México.
 * Formato es-MX: miles con coma y un decimal en los porcentajes.
 */

/** Lo que se pinta cuando no hay dato. */
export const SIN_DATO = "—";

export const ZONA_MEXICO = "America/Mexico_City";

const ENTERO = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const UN_DECIMAL = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const FECHA_HORA = new Intl.DateTimeFormat("es-MX", {
  timeZone: ZONA_MEXICO, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
});
const FECHA = new Intl.DateTimeFormat("es-MX", { timeZone: ZONA_MEXICO, day: "2-digit", month: "2-digit", year: "numeric" });
const HORA = new Intl.DateTimeFormat("es-MX", { timeZone: ZONA_MEXICO, hour: "2-digit", minute: "2-digit", hour12: false });
const MINUTO = 60 * 1000;
const HORA_MS = 60 * MINUTO;
const DIA_MS = 24 * HORA_MS;
const PRIMERA_LETRA_MAYUSCULA = /^./;
/** Lo que manda SonarQube cuando no reconoce la integración continua, y cómo se dice aquí. */
const CI_SIN_DETECTAR = "undetected";
const CI_NO_DETECTADO = "no detectado";
/** Lo que manda SonarQube como versión cuando el análisis no trae una. */
const VERSION_SIN_DATO = "not provided";

/** Una medida como llega: texto crudo de SonarQube, número o nada. */
export type Medida = string | number | null | undefined;

/** Un número finito o nulo. */
export function numero(valor: Medida): number | null {
  if (valor == null || valor === "") {
    return null;
  }
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

/** "7582" → "7,582"; sin dato, `vacio`. */
export function entero(valor: Medida, vacio = SIN_DATO): string {
  const n = numero(valor);
  return n == null ? vacio : ENTERO.format(Math.round(n));
}

/** "99.7" → "99.7 %" (un decimal, espacio fino antes del signo); sin dato, `vacio`. */
export function pct(valor: Medida, vacio = SIN_DATO): string {
  const n = numero(valor);
  return n == null ? vacio : `${UN_DECIMAL.format(n)} %`;
}

/** Calificación de SonarQube ("1.0".."5.0" o ya la letra) → "A".."E"; sin dato, `vacio`. */
export function letra(valor: Medida, vacio = SIN_DATO): string {
  if (typeof valor === "string" && /^[A-E]$/.test(valor)) {
    return valor;
  }
  const n = numero(valor);
  if (n == null || n < 1 || n > 5) {
    return vacio;
  }
  return String.fromCodePoint(64 + Math.round(n));
}

/** Un texto ISO (UTC) a fecha; nula si no se entiende. */
export function aFecha(texto: string | null | undefined): Date | null {
  if (!texto) {
    return null;
  }
  const fecha = new Date(texto);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

/** "2026-10-07T15:08:00Z" → "07/10/2026 09:08" (hora de México); `vacio` si no hay fecha. */
export function fechaHora(texto: string | null | undefined, vacio = SIN_DATO): string {
  const fecha = aFecha(texto);
  return fecha ? FECHA_HORA.format(fecha).replace(",", "") : vacio;
}

/** "2026-10-07T15:08:00Z" → "07/10/2026"; `vacio` si no hay fecha. */
export function fechaCorta(texto: string | null | undefined, vacio = SIN_DATO): string {
  const fecha = aFecha(texto);
  return fecha ? FECHA.format(fecha) : vacio;
}

/** "2026-10-07T15:08:00Z" → "09:08" (hora de México); `vacio` si no hay fecha. */
export function hora(texto: string | null | undefined, vacio = SIN_DATO): string {
  const fecha = aFecha(texto);
  return fecha ? HORA.format(fecha) : vacio;
}

/** La fecha de `texto` es el día de hoy en México. */
export function esHoy(texto: string | null | undefined, ahora: Date = new Date()): boolean {
  const fecha = aFecha(texto);
  return !!fecha && FECHA.format(fecha) === FECHA.format(ahora);
}

/**
 * "hace un momento", "hace 35 min", "hace 1 h", "hace 412 días", "hace 2 años"
 * (en días hasta los dos años, como diasEnTexto, para no perder precisión);
 * vacío sin fecha. Para el chip de antigüedad del tablero y el encabezado del
 * detalle.
 */
export function haceCuanto(texto: string | null | undefined, ahora: Date = new Date()): string {
  const fecha = aFecha(texto);
  if (!fecha) {
    return "";
  }
  const ms = Math.max(0, ahora.getTime() - fecha.getTime());
  if (ms < MINUTO) {
    return "hace un momento";
  }
  if (ms < HORA_MS) {
    return `hace ${Math.floor(ms / MINUTO)} min`;
  }
  if (ms < DIA_MS) {
    return `hace ${Math.floor(ms / HORA_MS)} h`;
  }
  return `hace ${diasEnTexto(Math.floor(ms / DIA_MS))}`;
}

/** "177 días", "1 día", "2 años": cuántos días lleva sin analizarse, para el chip ámbar. */
export function diasEnTexto(dias: number | null | undefined): string {
  if (dias == null) {
    return "";
  }
  if (dias >= 730) {
    return `${Math.floor(dias / 365)} años`;
  }
  return `${entero(dias)} ${dias === 1 ? "día" : "días"}`;
}

/** Los primeros 7 caracteres de una revisión de git. */
export function commitCorto(revision: string | null | undefined): string {
  return (revision ?? "").trim().slice(0, 7);
}

/** Los primeros 12 caracteres de un SHA-256, como se imprime en el pie de los reportes. */
export function huellaCorta(huella: string | null | undefined): string {
  return (huella ?? "").trim().slice(0, 12);
}

/* ---------- Métricas: nombres, tipos y formato ---------- */

/** Cómo se lee cada métrica: porcentaje, calificación o conteo. */
export type TipoMetrica = "PCT" | "LETRA" | "ENTERO";

const PCT_METRICAS = new Set([
  "coverage", "new_coverage", "line_coverage", "new_line_coverage", "branch_coverage", "new_branch_coverage",
  "duplicated_lines_density", "new_duplicated_lines_density", "security_hotspots_reviewed",
  "new_security_hotspots_reviewed", "test_success_density",
]);
const LETRA_METRICAS = new Set([
  "software_quality_reliability_rating", "software_quality_security_rating", "software_quality_maintainability_rating",
  "new_software_quality_reliability_rating", "new_software_quality_security_rating",
  "new_software_quality_maintainability_rating", "reliability_rating", "security_rating", "sqale_rating",
  "new_reliability_rating", "new_security_rating", "new_maintainability_rating", "security_review_rating",
  "new_security_review_rating",
]);

/** Nombres legibles por clave de métrica (el detalle trae el suyo del servidor; el tablero solo la clave). */
const NOMBRES: Readonly<Record<string, string>> = {
  new_violations: "Hallazgos nuevos",
  violations: "Hallazgos abiertos",
  new_coverage: "Cobertura del código nuevo",
  coverage: "Cobertura",
  new_line_coverage: "Cobertura de líneas del código nuevo",
  line_coverage: "Cobertura de líneas",
  new_branch_coverage: "Cobertura de condiciones del código nuevo",
  branch_coverage: "Cobertura de condiciones",
  new_duplicated_lines_density: "Duplicación del código nuevo",
  duplicated_lines_density: "Duplicación",
  new_security_hotspots_reviewed: "Hotspots revisados del código nuevo",
  security_hotspots_reviewed: "Hotspots revisados",
  new_reliability_rating: "Confiabilidad del código nuevo",
  reliability_rating: "Confiabilidad",
  new_security_rating: "Seguridad del código nuevo",
  security_rating: "Seguridad",
  new_maintainability_rating: "Mantenibilidad del código nuevo",
  sqale_rating: "Mantenibilidad",
  new_software_quality_reliability_rating: "Confiabilidad del código nuevo",
  software_quality_reliability_rating: "Confiabilidad",
  new_software_quality_security_rating: "Seguridad del código nuevo",
  software_quality_security_rating: "Seguridad",
  new_software_quality_maintainability_rating: "Mantenibilidad del código nuevo",
  software_quality_maintainability_rating: "Mantenibilidad",
  new_security_review_rating: "Revisión de seguridad del código nuevo",
  security_review_rating: "Revisión de seguridad",
  skipped_tests: "Pruebas omitidas",
  test_success_density: "Éxito de las pruebas",
  test_errors: "Pruebas con error",
  test_failures: "Pruebas fallidas",
  tests: "Pruebas",
  accepted_issues: "Hallazgos aceptados",
  new_accepted_issues: "Hallazgos aceptados en el código nuevo",
  security_hotspots: "Hotspots de seguridad",
  new_security_hotspots: "Hotspots de seguridad nuevos",
  ncloc: "Líneas de código",
  new_lines: "Líneas nuevas",
};

export function tipoMetrica(metrica: string): TipoMetrica {
  if (PCT_METRICAS.has(metrica)) {
    return "PCT";
  }
  return LETRA_METRICAS.has(metrica) ? "LETRA" : "ENTERO";
}

/** El nombre legible de una métrica; la clave tal cual si no se conoce. */
export function nombreMetrica(metrica: string): string {
  return NOMBRES[metrica] ?? metrica;
}

/** Un valor crudo de SonarQube con el formato de su métrica. */
export function valorMetrica(metrica: string, valor: Medida, vacio = SIN_DATO): string {
  switch (tipoMetrica(metrica)) {
    case "PCT":
      return pct(valor, vacio);
    case "LETRA":
      return letra(valor, vacio);
    default:
      return entero(valor, vacio);
  }
}

/** El símbolo con que se lee el umbral: GT falla arriba del umbral (debe ser ≤), LT falla abajo (debe ser ≥). */
function simboloUmbral(operador: OperadorCc, tipo: TipoMetrica, umbral: number | null): string {
  if (tipo === "LETRA" || umbral === 0) {
    return "";
  }
  return operador === "GT" ? "≤ " : "≥ ";
}

/** "≥ 95 %", "≤ 3 %", "A", "0": el umbral de un criterio como se lee. */
export function textoUmbral(condicion: Pick<Condicion, "metrica" | "operador" | "umbral">): string {
  const tipo = tipoMetrica(condicion.metrica);
  const n = numero(condicion.umbral);
  const valor = valorMetrica(condicion.metrica, condicion.umbral, condicion.umbral || SIN_DATO);
  const umbralPct = tipo === "PCT" && n != null && Number.isInteger(n) ? `${ENTERO.format(n)} %` : valor;
  return `${simboloUmbral(condicion.operador, tipo, n)}${tipo === "PCT" ? umbralPct : valor}`;
}

/** "debe ser ≥ 95 %". */
export function debeSer(condicion: Pick<Condicion, "metrica" | "operador" | "umbral">): string {
  return `debe ser ${textoUmbral(condicion)}`;
}

/** La primera letra en mayúscula ("hace 1 h" → "Hace 1 h"). */
export function mayuscula(texto: string): string {
  return texto.replace(PRIMERA_LETRA_MAYUSCULA, (c) => c.toUpperCase());
}

/** Los conteos de criterios con ceros donde el servidor no mandó nada (un proyecto sin análisis puede traerlos nulos). */
export function conteoCriterios(c: Partial<ConteoCriterios> | null | undefined): ConteoCriterios {
  return { total: c?.total ?? 0, pasan: c?.pasan ?? 0, fallan: c?.fallan ?? 0, sinDato: c?.sinDato ?? 0, ignoradas: c?.ignoradas ?? 0 };
}

/** "pasan 14 de 15", "falla 1 de 4", "fallan 4 de 4", "pasa 1 de 4" (como el boceto). */
export function textoCriterios(conteo: Partial<ConteoCriterios> | null | undefined): string {
  const c = conteoCriterios(conteo);
  if (c.total === 0) {
    return "sin criterios";
  }
  if (c.fallan > 0) {
    return `${c.fallan === 1 ? "falla" : "fallan"} ${c.fallan} de ${c.total}`;
  }
  return `${c.pasan === 1 ? "pasa" : "pasan"} ${c.pasan} de ${c.total}`;
}

/** "A", "A y B", "A, B y C" (hasta `tope`), "A, B y 3 más". */
export function nombres(lista: string[], tope = 2): string {
  if (lista.length <= tope) {
    return lista.length < 2 ? lista.join("") : `${lista.slice(0, -1).join(", ")} y ${lista.at(-1)}`;
  }
  return `${lista.slice(0, tope).join(", ")} y ${lista.length - tope} más`;
}

/** El periodo de código nuevo de SonarQube, en español: "análisis fijado", "versión anterior", "últimos 30 días", "rama main". */
export function nombrePeriodo(tipo: string | null | undefined, valor: string | null | undefined): string {
  switch (tipo) {
    case "SPECIFIC_ANALYSIS":
      return "análisis fijado";
    case "PREVIOUS_VERSION":
      return "versión anterior";
    case "NUMBER_OF_DAYS":
      return valor ? `últimos ${valor} días` : "últimos días";
    case "REFERENCE_BRANCH":
      return `rama ${valor ?? "de referencia"}`;
    default:
      return (tipo ?? "").toLowerCase();
  }
}

/**
 * La integración continua que detectó SonarQube, como se lee: "Azure DevOps"
 * o "no detectado". El servidor ya manda «no detectado»; una foto vieja (un
 * folio emitido antes) puede traer el «undetected» original, con cualquier
 * mayúscula. Vacío si no hay dato.
 */
export function textoCi(ci: string | null | undefined): string {
  const texto = (ci ?? "").trim();
  return texto.toLowerCase() === CI_SIN_DETECTAR ? CI_NO_DETECTADO : texto;
}

/**
 * La versión del proyecto, como se lee: "0.0", "tandas-0510". Vacío si no hay
 * dato o si llega el «not provided» que manda SonarQube cuando el análisis no
 * trae versión (en inglés y sin información: no se enseña).
 */
export function textoVersion(version: string | null | undefined): string {
  const texto = (version ?? "").trim();
  return texto.toLowerCase() === VERSION_SIN_DATO ? "" : texto;
}

/** "1 proyecto" / "12 proyectos". */
export function plural(n: number, singular: string, pluralTexto: string): string {
  return `${entero(n)} ${n === 1 ? singular : pluralTexto}`;
}
